import { CatalogBghTileItem } from '~/features/catalog/components/CatalogBghTileItem';
import type { AghArticle } from '~/common/types/aghArticle';
import type { BghArticle } from '~/common/types/bghArticle';
import type { ArticleType } from '~/common/types/baseArticle';
import { Virtuoso } from 'react-virtuoso';
import { CatalogAghTileItem } from '~/features/catalog/components/CatalogAghTileItem';
import { useMemo } from 'react';
import { cn } from '~/common/utils/styleUtil';
import { useScreenSize } from '~/common/hooks/useScreenSize';

interface CatalogTilesProps {
  isAuthenticated: boolean;
  articles: AghArticle[] | BghArticle[];
  articleType: ArticleType;
}

// Render-Funktion auf Modulebene (Sonar S6478); benötigte Werte kommen über das Virtuoso context-Prop
type CatalogTilesContext = {
  rows: any[];
  noOfItemsPerRow: number;
  articleType: ArticleType;
  isAuthenticated: boolean;
};

const GRID_COLS_BY_ITEMS_PER_ROW: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5'
};

function renderCatalogTileRow(index: number, _data: unknown, context: CatalogTilesContext) {
  const { rows, noOfItemsPerRow, articleType, isAuthenticated } = context;
  const row = rows[index];
  return (
    <div key={index} className={cn('grid pt-4 divide-x', GRID_COLS_BY_ITEMS_PER_ROW[noOfItemsPerRow] ?? 'grid-cols-6')}>
      {row.map(article => (
        <div key={article.articleNumber} className={'min-w-[265px] '}>
          {articleType === 'bgh' ? (
            <CatalogBghTileItem
              key={(article as BghArticle).articleNumber}
              isSingleItemInRow={noOfItemsPerRow === 1}
              article={article as BghArticle}
              isAuthenticated={isAuthenticated}
            />
          ) : (
            <CatalogAghTileItem
              key={(article as AghArticle).articleNumber}
              isSingleItemInRow={noOfItemsPerRow === 1}
              article={article}
              isAuthenticated={isAuthenticated}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default function CatalogTiles({ isAuthenticated, articles, articleType }: Readonly<CatalogTilesProps>) {
  const { isMobile, screenSize } = useScreenSize();

  const noOfItemsPerRow = useMemo(() => {
    const lostSpace = 360; // Navigation and layout paddings
    const minTileWidth = 265;
    const screenWidth = Math.min(screenSize.width, 1920);
    const contentWith = isMobile ? screenSize.width : screenWidth - lostSpace;

    return contentWith < lostSpace ? 1 : Math.floor(contentWith / minTileWidth);
  }, [isMobile, screenSize]);

  const rows: any = [];
  for (let i = 0; i < articles.length; i += noOfItemsPerRow) {
    rows.push(articles.slice(i, i + noOfItemsPerRow));
  }
  return (
    <div className="pb-12">
      <Virtuoso
        totalCount={rows.length}
        useWindowScroll
        overscan={1500}
        context={{ rows, noOfItemsPerRow, articleType, isAuthenticated }}
        itemContent={renderCatalogTileRow}
      />
    </div>
  );
}
